import type { BlogPost } from '@/hooks/useBlogPosts';

interface BlogCardProps {
  post: BlogPost;
}

export default function BlogCard({ post }: BlogCardProps) {
  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const shortDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  return (
    <article className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1">
      <a href={`${pathPrefix}/blog/${post.slug}`} className="block overflow-hidden">
        {post.coverImage ? (
          <div className="relative w-full h-52 overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        ) : (
          <div className="w-full h-52 bg-background-100 flex items-center justify-center">
            <span className="w-12 h-12 flex items-center justify-center">
              <i className="ri-article-line text-3xl text-foreground-300" />
            </span>
          </div>
        )}
      </a>
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-3 mb-3">
          {post.category && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
              {post.category}
            </span>
          )}
          {formattedDate && (
            <span className="text-xs text-foreground-500 whitespace-nowrap">{formattedDate}</span>
          )}
        </div>
        <a href={`${pathPrefix}/blog/${post.slug}`} className="group/link">
          <h2 className="font-heading text-lg font-semibold text-foreground-900 leading-snug mb-2 transition-colors group-hover/link:text-primary-600 line-clamp-2">
            {post.title}
          </h2>
        </a>
        {post.excerpt && (
          <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-3">
            {post.excerpt}
          </p>
        )}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.tags.map((tag) => (
              <a
                key={tag}
                href={`${pathPrefix}/blog/tag/${encodeURIComponent(tag)}`}
                className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 border border-background-200/70 whitespace-nowrap transition-colors hover:bg-primary-100 hover:text-primary-700 hover:border-primary-200 cursor-pointer relative z-10"
              >
                {tag}
              </a>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-auto pt-3 border-t border-background-200/70">
          {shortDate && (
            <span className="text-xs text-foreground-600 font-medium flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line text-xs" />
              </span>
              {shortDate}
            </span>
          )}
          <a
            href={`${pathPrefix}/blog/${post.slug}`}
            className="ml-auto text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            Read more
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs" />
            </span>
          </a>
        </div>
      </div>
    </article>
  );
}